import { useState, useEffect } from "react";
import { useAuth, API } from "../contexts/AuthContext";
import { Link } from "react-router-dom";
import axios from "axios";
import { History, Trophy, ChevronLeft, Loader2, Users, Star } from "lucide-react";

export default function DailyHistoryPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    const abortController = new AbortController();
    
    const fetchHistory = async () => {
      if (!user) return;
      setLoading(true);
      try {
        const token = await user.getIdToken();
        const res = await axios.get(`${API}/daily/teams/my-history?page=${page}&limit=20`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: abortController.signal
        });
        
        if (abortController.signal.aborted) return;
        
        const teams = res.data.history || [];
        setHistory(prev => page === 1 ? teams : [...prev, ...teams]);
        setHasMore(teams.length === 20);
      } catch (e) {
        if (e.name !== 'CanceledError') {
          console.error("Failed to fetch daily history:", e);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };
    fetchHistory();
    
    return () => abortController.abort();
  }, [user, page]);

  if (loading && page === 1) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="animate-spin text-amber-500" size={48} />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-6" data-testid="daily-history-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link to="/daily" className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
          <ChevronLeft className="text-slate-400" size={20} />
        </Link>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
            <History className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">My Daily Teams</h1>
            <p className="text-sm text-slate-400">Your Match Day history</p>
          </div>
        </div>
      </div>

      {/* History List */}
      {history.length === 0 ? (
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-8 text-center">
          <History className="mx-auto text-slate-600 mb-4" size={48} />
          <p className="text-slate-400 mb-2">No Match Day teams yet</p>
          <p className="text-slate-500 text-sm mb-4">Start playing to see your history here!</p>
          <Link 
            to="/daily" 
            className="inline-block px-4 py-2 bg-amber-500 text-black font-medium rounded-lg hover:bg-amber-400 transition-colors"
          >
            Go to Match Day
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {history.map((team) => (
            <DailyTeamCard key={team.id} team={team} />
          ))}
          
          {hasMore && (
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={loading}
              className="w-full py-3 bg-slate-800 border border-slate-700 rounded-xl text-slate-300 hover:bg-slate-700 transition-colors disabled:opacity-50"
            >
              {loading ? <Loader2 className="animate-spin mx-auto" size={20} /> : "Load More"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function DailyTeamCard({ team }) {
  const [expanded, setExpanded] = useState(false);
  const hasPoints = team.points !== null && team.points !== undefined;
  
  return (
    <div 
      className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden"
      data-testid={`daily-team-${team.match_id}`}
    >
      {/* Header - Always visible */}
      <div 
        className="p-4 cursor-pointer hover:bg-slate-800/80 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white font-semibold">
              {team.team1_short || team.team1} vs {team.team2_short || team.team2}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">{team.match_date || team.created_at?.split("T")[0]}</p>
          </div>
          <div className="text-right">
            {hasPoints ? (
              <>
                <p className="text-xl font-bold text-amber-400">{team.points}</p>
                <p className="text-xs text-slate-500">points</p>
              </>
            ) : (
              <span className="text-xs text-slate-500 bg-slate-700/50 px-2 py-1 rounded">Pending</span>
            )}
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="px-4 pb-4 pt-2 border-t border-slate-700/50">
          <div className="flex items-center gap-2 mb-3">
            <Users size={14} className="text-slate-400" />
            <span className="text-sm text-slate-400">Your Squad</span>
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            {(team.player_names || []).map((name, idx) => {
              const playerId = team.players?.[idx];
              const isCaptain = playerId === team.captain_id;
              const isVC = playerId === team.vice_captain_id;
              
              return (
                <div 
                  key={idx} 
                  className={`px-2 py-1.5 rounded text-sm flex items-center gap-1.5 ${
                    isCaptain ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
                    isVC ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' :
                    'bg-slate-700/50 text-slate-300'
                  }`}
                >
                  {isCaptain && <Star size={12} className="text-yellow-400" />}
                  {isVC && <Star size={12} className="text-cyan-400" />}
                  <span className="truncate">{name}</span>
                  {isCaptain && <span className="text-[10px] ml-auto">(C)</span>}
                  {isVC && <span className="text-[10px] ml-auto">(VC)</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
